import React, {ReactElement, useCallback, useMemo, useRef, useState} from "react";
import ZeeroSelect, {TOption} from "../../custom/ZeeroSelect";
import ZeeroDatePicker, {ZEERO_DATE_FORMAT} from "../../custom/ZeeroDatePicker";
import ZeeroButton from "../../custom/ZeeroButton";
import * as http from "../../../handlers/http"
import {EventLog, EventLogType} from "../../../types/EventLog";
import ZeeroLoading from "../../custom/ZeeroLoading";
import {ColumnDef, ZeeroDataTable} from "../../custom/ZeeroDataTable";
import {faArrowDownWideShort, faArrowUpShortWide} from "@awesome.me/kit-92187fb46c/icons/classic/regular";
import moment, {Moment} from "moment";
import {toast} from "../../custom/toasters/ToastersManager";
import {useTranslation} from "react-i18next";

const {FontAwesomeIcon} = await import("@fortawesome/react-fontawesome");

// todo ISU-111: handle highlighting
export default function EventLogsPage(): ReactElement {
	const {t} = useTranslation();
	const [eventsRes, setEventsRes] = useState<{ limited: boolean, logs: EventLog[] }>({limited: false, logs: []});
	const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
	const [loading, setLoading] = useState<boolean>(false);
	const [alreadyLoaded, setAlreadyLoaded] = useState<boolean>(false);
	const [textSearch, setTextSearch] = useState<string>();

	const onSearch = (identifier: string, from: string, to: string, logTypes: string[]) => {
		setLoading(true)
		setTextSearch(identifier);
		setSortDirection("DESC");

		const logRequest = {identifier, from, to, logTypes};
		http.post<{ limited: boolean, logs: EventLog[] }>("/web/admin/event-logs", logRequest)
				.then((res) => {
					setEventsRes({limited: res.limited, logs: res.logs});
				})
				.catch(() => {
					toast.error(t("eventLogs_errorLoading"))
				})
				.finally(() => {
					setLoading(false);
					setAlreadyLoaded(true)
				})
	}

	const parseTime = (dateString: string): Date | null => {
		if (!dateString) {
			return null;
		}
		const [datePart, timePart] = dateString.split(' ');
		if (!datePart || !timePart) {
			return null;
		}

		const [day, month, year] = datePart.split('/').map(Number);
		const [hours, minutes, seconds] = timePart.split(':').map(Number);

		const date = new Date(year, month - 1, day, hours, minutes, seconds);
		return isNaN(date.getTime()) ? null : date;
	};

	const sortEntries = useCallback(() => {
		const {logs} = eventsRes;
		if (!logs || logs.length === 0) {
			return;
		}

		const nextOrder: 'ASC' | 'DESC' = sortDirection === 'DESC' ? 'ASC' : 'DESC';

		const sortedLogs = [...logs].sort((a, b) => {
			const dateA = parseTime(a.time);
			const dateB = parseTime(b.time);

			if (!dateA && !dateB) {
				return 0;
			}
			if (!dateA) {
				return nextOrder === 'ASC' ? 1 : -1;
			}
			if (!dateB) {
				return nextOrder === 'ASC' ? -1 : 1;
			}

			const timeA = dateA.getTime();
			const timeB = dateB.getTime();

			return nextOrder === 'DESC' ? timeB - timeA : timeA - timeB;
		});

		setEventsRes(prev => ({...prev, logs: sortedLogs}));
		setSortDirection(nextOrder);
	}, [eventsRes.logs, sortDirection]);


	const columns: ColumnDef<EventLog>[] = useMemo(() => [
		{
			id: "id",
			header: t("eventLogs_eventID"),
			cell: ({row}) => <span>{row.id}</span>,
			size: "5%",
		},
		{
			id: "type",
			header: t("general_type"),
			cell: ({row}) => <EventLogTypePill type={row.type}/>,
			size: "15%",
		},
		{
			id: "time",
			header: (
					<span className="event-logs-time-order">{t("eventLogs_generatedAt")}
						<ZeeroButton type="none" onClick={sortEntries}>
							<FontAwesomeIcon
									icon={sortDirection == "DESC" ? faArrowDownWideShort : faArrowUpShortWide}/>
						</ZeeroButton>
					</span>
			),
			cell: ({row}) => <span>{row.time}</span>,
			size: "15%",
		},
		{
			id: "context-participants",
			header: t("eventLogs_eventData"),
			cell: ({row}) => (
					<div className="event-log-data-cell">
						<div className={textSearch != undefined && row.context.includes(textSearch) ? "highlight" : ""}>{row.context}</div>
						<div>
							{row.participants
									.sort((a, b) => a.type.localeCompare(b.type))
									.map(participant => {

										return (
												<span className="event-log-data-participants"
													  key={`${participant.eventId}_${participant.type}`}>
												<strong>{participant.type}</strong>: {" "}
													<span
															className={textSearch != undefined && participant.value.includes(textSearch) ? "highlight" : ""}
													>
														{participant.value}
													</span>
											</span>
										)
									})}

						</div>
					</div>
			),
		},
	], [eventsRes, sortDirection, textSearch]);

	return (
			<div className="page-content">
				<div className="page-title">{t("eventLogs_eventLogs")}</div>

				<EventLogFilters onSearch={onSearch}/>
				{loading && <ZeeroLoading className="configurations" message={"Loading event logs"}/>}
				{!loading && alreadyLoaded && (
						<React.Fragment>
							{eventsRes.logs.length == 0 && <div id="event-logs-result-label">{t("eventLogs_noEventsFound")}</div>}
							{eventsRes.logs.length > 0 && (
									<React.Fragment>
										<div id="event-logs-result-label">
											{t("eventLogs_foundEvents", {count: eventsRes.logs.length})} {eventsRes.limited && t("eventLogs_searchLimit")}
										</div>
										<ZeeroDataTable<EventLog>
												rows={eventsRes.logs}
												getRowId={log => String(log.id)}
												columns={columns}
												maxHeight={"65vh"}
												onValueChange={() => {
												}}
										/>
									</React.Fragment>
							)}
						</React.Fragment>
				)}
			</div>
	)
}

// use #7D8CA3, #9A8FBF, #6FA8A3, #C48E8E, #8FAE94, #A6788E, #708B9C, #C97D73, #8B9E8A, #A09BB2. these are some nice pastel colors
const EventLogTypePill = ({type}: { type: EventLogType }): ReactElement => {

	const pillColor = useMemo(() => {
		switch (type) {
			case EventLogType.SESSION_AUTHORIZED:
				return "#7D8CA3";
			case EventLogType.SESSION_UNAUTHORIZED:
				return "#C97D73";
			case EventLogType.UPLOAD_FINISHED:
				return "#6FA8A3";
			case EventLogType.API_KEY_CREATED:
				return "#8FAE94";
			case EventLogType.API_KEY_UPDATED:
				return "#A09BB2";
			case EventLogType.API_KEY_DELETED:
				return "#C48E8E";
			default:
				return "var(--dark-grey)"
		}
	}, [type]);

	return <span className="event-log-type-cell" style={{backgroundColor: pillColor}}>{type}</span>;
}

const EventLogFilters = ({onSearch}: {
	onSearch: (identifier: string, from: string, to: string, logTypes: string[]) => void
}) => {
	const {t} = useTranslation();
	const eventLogTypes: TOption[] = useMemo(() => [
		{
			name: t("eventLogs_typeSessionAuthorized"),
			value: EventLogType.SESSION_AUTHORIZED,
			checked: false,
		},
		{
			name: t("eventLogs_typeSessionUnauthorized"),
			value: EventLogType.SESSION_UNAUTHORIZED,
			checked: false,
		},
		{
			name: t("eventLogs_typeUploadFinished"),
			value: EventLogType.UPLOAD_FINISHED,
			checked: false,
		},
		{
			name: t("eventLogs_typeAPIKeyCreated"),
			value: EventLogType.API_KEY_CREATED,
			checked: false,
		},
		{
			name: t("eventLogs_typeAPIKeyUpdated"),
			value: EventLogType.API_KEY_UPDATED,
			checked: false,
		},
		{
			name: t("eventLogs_typeAPIKeyDeleted"),
			value: EventLogType.API_KEY_DELETED,
			checked: false,
		},
	], []);

	const defaultEventLogTypesState = useCallback(() =>
			eventLogTypes.map(o => ({...o, checked: false})), [eventLogTypes]);

	const [from, setFrom] = useState<Moment | null>(moment());
	const [to, setTo] = useState<Moment | null>(moment());
	const identifierRef = useRef<HTMLInputElement>(null);
	const [identifier, setIdentifier] = useState<string | undefined>(undefined);
	const [logTypes, setLogTypes] = useState<TOption[]>(defaultEventLogTypesState());

	const onReset = () => {
		setFrom(null);
		setTo(null);
		setIdentifier(undefined);
		setLogTypes(defaultEventLogTypesState());
	}

	const handleSearch = () => {
		const identifierValid = identifier != undefined && identifier != "";
		if (!identifierValid) {
			identifierRef.current?.classList.add("invalid");
		}

		const datesValid = from != undefined && to != undefined
				&& from.isValid() && to.isValid()
				&& from.isSameOrBefore(to);
		if (!identifierValid || !datesValid) {
			return;
		}
		identifierRef.current?.classList.remove("invalid");

		let selectedTypes = logTypes
				.filter(option => option.checked)
				.map(option => option.value as string);
		onSearch(identifier, from?.format(ZEERO_DATE_FORMAT), to?.format(ZEERO_DATE_FORMAT), selectedTypes);
	}

	return (
			<div id="event-logs-filters">
				<div className="event-logs-filter">
					<span>{t("general_from")}: </span>
					<ZeeroDatePicker id="event-logs-from"
									 selectedDay={from}
									 setSelectedDay={setFrom}
									 allowedRange={{
										 min: moment("01/01/1800", ZEERO_DATE_FORMAT, true),
										 max: to ? to : moment("01/01/2500", ZEERO_DATE_FORMAT, true),
									 }}
					/>
					<div className="event-log-filter-required">({t("general_required")})</div>
				</div>
				<div className="event-logs-filter">
					<span>{t("general_to")}: </span>
					<ZeeroDatePicker
							id="event-logs-to"
							selectedDay={to}
							setSelectedDay={setTo}
							allowedRange={{
								min: from ? from : moment("01/01/2500", ZEERO_DATE_FORMAT, true),
								max: moment("01/01/2500", ZEERO_DATE_FORMAT, true),
							}}
					/>
					<div className="event-log-filter-required">({t("general_required")})</div>
				</div>
				<div className="event-logs-filter">
					<span>{t("eventLogs_filter")}: </span>
					<input
							ref={identifierRef}
							type="text"
							className="zeero-input"
							value={identifier ?? ""}
							onChange={(e) => setIdentifier(e.target.value)}
							onKeyUp={(e) => {
								if (e.key == "Enter") {
									handleSearch();
								}
							}}
					/>
					<div className="event-log-filter-required">({t("general_required")})</div>
				</div>
				<div className="event-logs-filter">
					<span>{t("eventLogs_logType")}: </span>
					<ZeeroSelect defaultOption={t("eventLogs_logTypeAll")}
								 multiple={true}
								 options={logTypes}
								 onOptionClick={(e) => setLogTypes(e.options)}
					/>
					<div className="event-log-filter-required">{" "}</div>
				</div>

				<div className="event-logs-filters-action">
					<ZeeroButton type="secondary" onClick={onReset}>
						{t("general_reset")}
					</ZeeroButton>
					<ZeeroButton type="primary" onClick={handleSearch}>
						{t("general_search")}
					</ZeeroButton>
				</div>
			</div>
	)
}